import {span, vbox} from '@xh/hoist/cmp/layout';
import {fmtNumber, fmtPercent, Precision} from '@xh/hoist/format';
import classNames from 'classnames';
import {ReactNode} from 'react';
import './Kpi.scss';

/**
 * Summary building blocks shared by the restyled tabs: a change chip and a KPI tile. Both take their
 * direction from a sign and their colors from Kpi.scss. The chip is colored text under the standard
 * style and a solid block under the terminal style.
 */

export type Dir = 'up' | 'down' | 'flat';

export function signDir(v: number): Dir {
    return v > 0 ? 'up' : v < 0 ? 'down' : 'flat';
}

export function signedMoney(v: number, precision: Precision = 0): string {
    return fmtNumber(v, {precision, prefix: '$', withPlusSign: true, nullDisplay: '-'}) as string;
}

/** Move as a chip with a direction glyph - the amount, then the percent. Omit either by passing null. */
export function changeChip(amount: ReactNode, pct: number, dir: Dir, className: string = null) {
    return span({
        className: classNames('rzx-chip', `rzx-chip--${dir}`, className),
        items: [
            dir === 'up' ? '▲ ' : dir === 'down' ? '▼ ' : '',
            amount,
            pct != null ? span({className: 'rzx-chip__pct', item: fmtPercent(pct, {precision: 2, withPlusSign: true})}) : null
        ]
    });
}

/** Money move as a chip, colored by the sign of the amount. */
export function moneyChangeChip(amount: number, pct: number, className: string = null) {
    return changeChip(signedMoney(amount), pct, signDir(amount), className);
}

/** Labeled value with a colored left edge for its direction. A null direction keeps the neutral edge. */
export function kpiTile(label: ReactNode, value: ReactNode, dir: Dir = null, className: string = null, title: string = null) {
    return vbox({
        className: classNames('rzx-kpi', dir && `rzx-kpi--${dir}`, className),
        title,
        items: [span({className: 'rzx-kpi__label', item: label}), span({className: 'rzx-kpi__value', item: value})]
    });
}
